export const ARRAY_FILTER_KEYS = new Set<string>([
  'origem',
  'canal',
  'qualificacao',
  'profissao',
  'tech_ja_conhece',
  'tech_busca_formacao',
  'empresa_id',
  'contato_cargo',
  'localizacao',
  'stage_id',
  'stage_sdr_id',
  'stage_closer_id',
  'sdr_id',
  'closer_id',
  'atendimento',
  'atendente_id',
  'loss_reason',
  'tags',
  'tags_excluir',
]);

export const BOOLEAN_FILTER_KEYS = new Set<string>([
  'reagend',
  'confirmed',
  'has_meeting',
  'is_turbo',
  'tags_all',
  'sem_tags',
  'has_email',
  'has_phone',
]);

export const MODAL_FILTER_KEYS: string[] = [
  'origem',
  'canal',
  'qualificacao',
  'profissao',
  'renda_min',
  'renda_max',
  'pat_min',
  'pat_max',
  'pontuacao_min',
  'pontuacao_max',
  'tech_ja_conhece',
  'tech_busca_formacao',
  'empresa_id',
  'contato_cargo',
  'localizacao',
  'func_min',
  'func_max',
  'empresa_fat_min',
  'empresa_fat_max',
  'has_email',
  'has_phone',
  'lead_status',
  'created_from',
  'created_to',
  'funnel_id',
  'pipeline_id',
  'stage_id',
  'stage_sdr_id',
  'stage_closer_id',
  'card_status',
  'reagend',
  'confirmed',
  'sdr_id',
  'closer_id',
  'atendimento_conversa',
  'atendimento',
  'atendente_id',
  'has_meeting',
  'meeting_status',
  'meeting_type',
  'meeting_from',
  'meeting_to',
  'presentation_from',
  'presentation_to',
  'fat_min',
  'fat_max',
  'is_turbo',
  'won_from',
  'won_to',
  'loss_reason',
  'lost_from',
  'lost_to',
  'perdidos',
  'tags',
  'tags_logic',
  'tags_all',
  'tags_excluir',
  'sem_tags',
  'limite',
];

export interface FilterOption {
  value: string | number;
  label: string;
  description?: string | null;
  initials?: string | null;
  color?: string | null;
  avatarUrl?: string | null;
  role?: string | null;
}

export interface FilterField {
  key?: string;
  key_min?: string;
  key_max?: string;
  key_from?: string;
  key_to?: string;
  label: string;
  type?: string;
  tipo?: string;
  options?: FilterOption[];
  format?: 'currency' | 'number';
}

export interface FilterSection {
  fields?: FilterField[];
}

export interface FilterChip {
  id: string;
  label: string;
  value: string;
  keys: string[];
}

export type FilterValue = string | boolean | Array<string | number>;
export type FilterValues = Record<string, FilterValue>;

// Assinatura mínima de setSearchParams (react-router-dom) que usamos.
export type SetSearchParams = (
  nextInit: (prev: URLSearchParams) => URLSearchParams,
  navigateOpts?: { replace?: boolean; state?: unknown },
) => void;

export const getFieldKeys = (field: FilterField): string[] => [
  field.key,
  field.key_min,
  field.key_max,
  field.key_from,
  field.key_to,
].filter((k): k is string => Boolean(k));

export const normalizeFilterValues = (values: Record<string, unknown> = {}): FilterValues => {
  const out: FilterValues = {};
  Object.entries(values || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const clean = value.filter((v) => v !== '' && v != null) as Array<string | number>;
      if (clean.length > 0) out[key] = clean;
      return;
    }
    if (value === '' || value == null) return;
    if (typeof value === 'boolean') {
      out[key] = value;
      return;
    }
    out[key] = String(value);
  });
  return out;
};

export const countActiveFilters = (values: Record<string, unknown> = {}): number =>
  Object.keys(normalizeFilterValues(values)).length;

export const countActiveFilterFields = (
  config: FilterSection[] = [],
  values: Record<string, unknown> = {},
): number => {
  const normalized = normalizeFilterValues(values);
  return config.reduce((total, section) => total + (section.fields || []).filter(field =>
    getFieldKeys(field).some(key => normalized[key] != null)
  ).length, 0);
};

export const readFiltersFromSearchParams = (
  searchParams: URLSearchParams,
  keys: string[] = MODAL_FILTER_KEYS,
): FilterValues => {
  const values: FilterValues = {};
  keys.forEach(key => {
    if (ARRAY_FILTER_KEYS.has(key)) {
      const list = searchParams.getAll(key).filter(Boolean);
      if (list.length > 0) values[key] = list;
      return;
    }
    const raw = searchParams.get(key);
    if (raw == null || raw === '') return;
    if (BOOLEAN_FILTER_KEYS.has(key)) {
      values[key] = raw === 'true';
      return;
    }
    values[key] = raw;
  });
  return values;
};

export const replaceFilterParams = (
  setSearchParams: SetSearchParams,
  values: Record<string, unknown>,
  keys: string[] = MODAL_FILTER_KEYS,
): void => {
  const normalized = normalizeFilterValues(values);
  setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    keys.forEach(key => next.delete(key));
    Object.entries(normalized).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(item => next.append(key, String(item)));
      } else {
        next.set(key, String(value));
      }
    });
    return next;
  }, { replace: true });
};

export const removeFilterKeysFromParams = (
  setSearchParams: SetSearchParams,
  keys: string[],
): void => {
  setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    keys.forEach(key => next.delete(key));
    return next;
  }, { replace: true });
};

const formatMoneyShort = (value: string | number): string => {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value ?? '');
  if (number >= 1000000) return `R$${(number / 1000000).toFixed(number % 1000000 === 0 ? 0 : 1)}M`;
  if (number >= 1000) return `R$${Math.round(number / 1000)}k`;
  return `R$${number.toLocaleString('pt-BR')}`;
};

const formatNumber = (value: string | number): string =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString('pt-BR') : String(value ?? '');

const optionLabel = (options: FilterOption[] = [], value: string | number): string =>
  options.find(o => String(o.value) === String(value))?.label ?? String(value ?? '');

export const buildActiveFilterChips = (
  config: FilterSection[] = [],
  values: Record<string, unknown> = {},
): FilterChip[] => {
  const normalized = normalizeFilterValues(values);
  const chips: FilterChip[] = [];

  config.forEach(section => {
    (section.fields || []).forEach(field => {
      const type = field.type || field.tipo;
      if (type === 'range') {
        const min = field.key_min ? normalized[field.key_min] : undefined;
        const max = field.key_max ? normalized[field.key_max] : undefined;
        if (min || max) {
          const formatValue = field.format === 'number' ? formatNumber : formatMoneyShort;
          chips.push({
            id: `${field.key_min}-${field.key_max}`,
            label: field.label,
            value: `${min ? formatValue(min as string | number) : 'mín.'} – ${max ? formatValue(max as string | number) : 'máx.'}`,
            keys: getFieldKeys(field),
          });
        }
        return;
      }
      if (type === 'daterange') {
        const from = field.key_from ? normalized[field.key_from] : undefined;
        const to = field.key_to ? normalized[field.key_to] : undefined;
        if (from || to) {
          chips.push({
            id: `${field.key_from}-${field.key_to}`,
            label: field.label,
            value: `${from || 'inicio'} - ${to || 'fim'}`,
            keys: getFieldKeys(field),
          });
        }
        return;
      }
      const fieldKey = field.key;
      if (!fieldKey) return;
      const value = normalized[fieldKey];
      if (value == null) return;
      if (Array.isArray(value)) {
        chips.push({
          id: fieldKey,
          label: field.label,
          value: value.map(v => optionLabel(field.options, v)).join(', '),
          keys: [fieldKey],
        });
        return;
      }
      if (type === 'boolean') {
        chips.push({
          id: fieldKey,
          label: field.label,
          value: value === true || value === 'true' ? 'Sim' : 'Nao',
          keys: [fieldKey],
        });
        return;
      }
      chips.push({
        id: fieldKey,
        label: field.label,
        value: optionLabel(field.options, value as string | number),
        keys: [fieldKey],
      });
    });
  });

  return chips;
};
